import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import e from 'express'
import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'
import { Accordion, AccordionTab } from 'primereact/accordion'
import { CheckboxChangeEvent } from 'primereact/checkbox'
import { StepperRefAttributes } from 'primereact/stepper'
import { Tooltip } from 'primereact/tooltip'
import { classNames } from 'primereact/utils'

import {
  DropdownToggleable,
  InputTextToggleable,
  InputTextareaToggleable,
} from '../../../../../../components/inputs'
import {
  useNavBlockerContext,
  useToastContext,
} from '../../../../../../contexts'
import { EInvoiceDto } from '../../../../../../data/models'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config'
import { FieldOption } from '../../../../../ui-config/data'
import { ValueStatus } from '../../../../common/value-status'
import { requestUpdateEInvoice } from '../../../../data'
import { Footer } from '../footer'
import { LinesData } from './lines'

type Props = {
  eInvoice: EInvoiceDto | undefined
  isLoading: boolean
  stepperRef: React.MutableRefObject<StepperRefAttributes | null>
  onSubmit?: (data: AdditionalDataFormValues) => Promise<void>
}

interface AdditionalDataFormValues {
  buyerEmail: string
  buyerEmailChecked: boolean
  buyerName: string
  buyerNameChecked: boolean
  buyerRoutingId: string
  buyerRoutingIdChecked: boolean
  buyerPhone: string
  buyerPhoneChecked: boolean
  projectNumber: string
  projectNumberChecked: boolean
  contractNumber: string
  contractNumberChecked: boolean
  orderNumber: string
  orderNumberChecked: boolean
  workOrderNumber: string
  workOrderNumberChecked: boolean
  goodsReceiptNotification: string
  goodsReceiptNotificationChecked: boolean
  shippingNotification: string
  shippingNotificationChecked: boolean
  tender: string
  tenderChecked: boolean
  objectReference: string
  objectReferenceChecked: boolean
  bookingAccount: string
  bookingAccountChecked: boolean
  additionalRemarks: string
  additionalRemarksChecked: boolean
}

type FormFieldName = keyof AdditionalDataFormValues

// Helper function for normalizing values
const normalizeValue = (value: string | null | undefined): string => {
  return value ?? ''
}

// Helper function for comparing possibly null/undefined values
const hasValueChanged = (
  formValue: string,
  originalValue: string | null | undefined,
): boolean => {
  return normalizeValue(formValue) !== normalizeValue(originalValue)
}

export const AdditionalData: React.FC<Props> = ({
  eInvoice,
  isLoading,
  stepperRef,
}) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const { setBlocker } = useNavBlockerContext()

  const { uiConfig: config } = useConfigContext()

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)
  const lastBlockerState = React.useRef<boolean>(false)

  const handleSubmit = async (formValues: AdditionalDataFormValues) => {
    let hasAnyChanges = false

    const eInvoiceDto: EInvoiceDto = produce(eInvoice!, draft => {
      // BT-58 - Buyer email
      const buyerEmailChanged = hasValueChanged(
        formValues.buyerEmail,
        draft.Payload.AccountingCustomerParty.Party?.Contact?.ElectronicMail
          ?.OriginalValue,
      )
      const buyerEmailCheckboxChanged =
        formValues.buyerEmailChecked !==
        (eInvoice?.Payload?.AccountingCustomerParty.Party?.Contact
          ?.ElectronicMail?.IsEditEnabled || false)
      const buyerEmailShouldEdit =
        buyerEmailChanged || buyerEmailCheckboxChanged

      if (buyerEmailShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.AccountingCustomerParty.Party) {
          draft.Payload.AccountingCustomerParty.Party = {} as any
        }
        if (
          draft.Payload.AccountingCustomerParty.Party &&
          !draft.Payload.AccountingCustomerParty.Party.Contact
        ) {
          draft.Payload.AccountingCustomerParty.Party.Contact = {} as any
        }
        if (
          draft.Payload.AccountingCustomerParty.Party?.Contact &&
          !draft.Payload.AccountingCustomerParty.Party.Contact.ElectronicMail
        ) {
          draft.Payload.AccountingCustomerParty.Party.Contact.ElectronicMail = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (
          draft.Payload.AccountingCustomerParty.Party?.Contact?.ElectronicMail
        ) {
          draft.Payload.AccountingCustomerParty.Party.Contact.ElectronicMail.Value =
            formValues.buyerEmail
          draft.Payload.AccountingCustomerParty.Party.Contact.ElectronicMail.IsEditEnabled =
            formValues.buyerEmailChecked
        }
      }

      // BT-56 - Buyer Name
      const buyerNameChanged = hasValueChanged(
        formValues.buyerName,
        draft.Payload.AccountingCustomerParty.Party?.Contact?.Name
          ?.OriginalValue,
      )
      const buyerNameCheckboxChanged =
        formValues.buyerNameChecked !==
        (eInvoice?.Payload.AccountingCustomerParty.Party?.Contact?.Name
          ?.IsEditEnabled || false)
      const buyerNameShouldEdit = buyerNameChanged || buyerNameCheckboxChanged

      if (buyerNameShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.AccountingCustomerParty.Party) {
          draft.Payload.AccountingCustomerParty.Party = {} as any
        }
        if (
          draft.Payload.AccountingCustomerParty.Party &&
          !draft.Payload.AccountingCustomerParty.Party.Contact
        ) {
          draft.Payload.AccountingCustomerParty.Party.Contact = {} as any
        }
        if (
          draft.Payload.AccountingCustomerParty.Party?.Contact &&
          !draft.Payload.AccountingCustomerParty.Party.Contact.Name
        ) {
          draft.Payload.AccountingCustomerParty.Party.Contact.Name = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (draft.Payload.AccountingCustomerParty.Party?.Contact?.Name) {
          draft.Payload.AccountingCustomerParty.Party.Contact.Name.Value =
            formValues.buyerName
          draft.Payload.AccountingCustomerParty.Party.Contact.Name.IsEditEnabled =
            formValues.buyerNameChecked
        }
      }

      // BT-10 - Buyer Reference
      const buyerRoutingIdChanged = hasValueChanged(
        formValues.buyerRoutingId,
        draft.Payload.BuyerReference?.OriginalValue,
      )
      const buyerRoutingIdCheckboxChanged =
        formValues.buyerRoutingIdChecked !==
        (eInvoice?.Payload.BuyerReference?.IsEditEnabled || false)
      const buyerRoutingIdShouldEdit =
        buyerRoutingIdChanged || buyerRoutingIdCheckboxChanged

      if (buyerRoutingIdShouldEdit && !draft.Payload.BuyerReference) {
        draft.Payload.BuyerReference = {
          Value: formValues.buyerRoutingId,
          OriginalValue: '',
          IsEditEnabled: formValues.buyerRoutingIdChecked,
        } as any
      }

      if (buyerRoutingIdShouldEdit && draft.Payload.BuyerReference) {
        hasAnyChanges = true

        draft.Payload.BuyerReference.Value = formValues.buyerRoutingId
        draft.Payload.BuyerReference.IsEditEnabled =
          formValues.buyerRoutingIdChecked
      }

      // BT-57 - Buyer Phone
      const buyerPhoneChanged = hasValueChanged(
        formValues.buyerPhone,
        draft.Payload.AccountingCustomerParty.Party?.Contact?.Telephone
          ?.OriginalValue,
      )
      const buyerPhoneCheckboxChanged =
        formValues.buyerPhoneChecked !==
        (eInvoice?.Payload?.AccountingCustomerParty.Party?.Contact?.Telephone
          ?.IsEditEnabled || false)
      const buyerPhoneShouldEdit =
        buyerPhoneChanged || buyerPhoneCheckboxChanged

      if (buyerPhoneShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.AccountingCustomerParty.Party) {
          draft.Payload.AccountingCustomerParty.Party = {} as any
        }
        if (
          draft.Payload.AccountingCustomerParty.Party &&
          !draft.Payload.AccountingCustomerParty.Party.Contact
        ) {
          draft.Payload.AccountingCustomerParty.Party.Contact = {} as any
        }
        if (
          draft.Payload.AccountingCustomerParty.Party?.Contact &&
          !draft.Payload.AccountingCustomerParty.Party.Contact.Telephone
        ) {
          draft.Payload.AccountingCustomerParty.Party.Contact.Telephone = {
            Value: {
              Value: '',
              OriginalValue: '',
              IsEditEnabled: false,
            },
          } as any
        }
        if (
          draft.Payload.AccountingCustomerParty.Party?.Contact?.Telephone &&
          !draft.Payload.AccountingCustomerParty.Party.Contact.Telephone.Value
        ) {
          draft.Payload.AccountingCustomerParty.Party.Contact.Telephone.Value =
            {
              Value: '',
              OriginalValue: '',
              IsEditEnabled: false,
            } as any
        }

        hasAnyChanges = true

        if (draft.Payload.AccountingCustomerParty.Party?.Contact?.Telephone) {
          draft.Payload.AccountingCustomerParty.Party.Contact.Telephone.Value =
            formValues.buyerPhone
          draft.Payload.AccountingCustomerParty.Party.Contact.Telephone.IsEditEnabled =
            formValues.buyerPhoneChecked
        }
      }

      // BT-11 - Project Number
      const projectNumberChanged = hasValueChanged(
        formValues.projectNumber,
        draft.Payload.ProjectReference?.ID?.Value?.OriginalValue,
      )
      const projectNumberCheckboxChanged =
        formValues.projectNumberChecked !==
        (eInvoice?.Payload?.ProjectReference?.ID?.Value?.IsEditEnabled || false)
      const projectNumberShouldEdit =
        projectNumberChanged || projectNumberCheckboxChanged

      if (projectNumberShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.ProjectReference) {
          draft.Payload.ProjectReference = {} as any
        }
        if (
          draft.Payload.ProjectReference &&
          !draft.Payload.ProjectReference.ID
        ) {
          draft.Payload.ProjectReference.ID = {} as any
        }
        if (
          draft.Payload.ProjectReference?.ID &&
          !draft.Payload.ProjectReference.ID.Value
        ) {
          draft.Payload.ProjectReference.ID.Value = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (draft.Payload.ProjectReference.ID?.Value) {
          draft.Payload.ProjectReference.ID.Value.Value =
            formValues.projectNumber
          draft.Payload.ProjectReference.ID.Value.IsEditEnabled =
            formValues.projectNumberChecked
        }
      }

      // BT-12 - Contract Number
      const contractNumberChanged = hasValueChanged(
        formValues.contractNumber,
        draft.Payload.ContractDocumentReference?.ID?.Value?.OriginalValue,
      )
      const contractNumberCheckboxChanged =
        formValues.contractNumberChecked !==
        (eInvoice?.Payload?.ContractDocumentReference?.ID?.Value
          ?.IsEditEnabled || false)
      const contractNumberShouldEdit =
        contractNumberChanged || contractNumberCheckboxChanged

      if (contractNumberShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.ContractDocumentReference) {
          draft.Payload.ContractDocumentReference = {} as any
        }
        if (
          draft.Payload.ContractDocumentReference &&
          !draft.Payload.ContractDocumentReference.ID
        ) {
          draft.Payload.ContractDocumentReference.ID = {} as any
        }
        if (
          draft.Payload.ContractDocumentReference?.ID &&
          !draft.Payload.ContractDocumentReference.ID.Value
        ) {
          draft.Payload.ContractDocumentReference.ID.Value = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (draft.Payload.ContractDocumentReference?.ID?.Value) {
          draft.Payload.ContractDocumentReference.ID.Value.Value =
            formValues.contractNumber
          draft.Payload.ContractDocumentReference.ID.Value.IsEditEnabled =
            formValues.contractNumberChecked
        }
      }

      // BT-13 - Order Number
      const orderNumberChanged = hasValueChanged(
        formValues.orderNumber,
        draft.Payload.OrderReference?.ID?.Value?.OriginalValue,
      )
      const orderNumberCheckboxChanged =
        formValues.orderNumberChecked !==
        (eInvoice?.Payload?.OrderReference?.ID?.Value?.IsEditEnabled || false)
      const orderNumberShouldEdit =
        orderNumberChanged || orderNumberCheckboxChanged

      if (orderNumberShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.OrderReference) {
          draft.Payload.OrderReference = {} as any
        }
        if (draft.Payload.OrderReference && !draft.Payload.OrderReference.ID) {
          draft.Payload.OrderReference.ID = {} as any
        }
        if (
          draft.Payload.OrderReference?.ID &&
          !draft.Payload.OrderReference.ID.Value
        ) {
          draft.Payload.OrderReference.ID.Value = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (draft.Payload.OrderReference?.ID?.Value) {
          draft.Payload.OrderReference.ID.Value.Value = formValues.orderNumber
          draft.Payload.OrderReference.ID.Value.IsEditEnabled =
            formValues.orderNumberChecked
        }
      }

      // BT-14 - Work Order Number
      const workOrderNumberChanged = hasValueChanged(
        formValues.workOrderNumber,
        draft.Payload.OrderReference?.SalesOrderID?.OriginalValue,
      )
      const workOrderNumberCheckboxChanged =
        formValues.workOrderNumberChecked !==
        (eInvoice?.Payload?.OrderReference?.SalesOrderID?.IsEditEnabled ||
          false)
      const workOrderNumberShouldEdit =
        workOrderNumberChanged || workOrderNumberCheckboxChanged

      if (workOrderNumberShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.OrderReference) {
          draft.Payload.OrderReference = {} as any
        }
        if (
          draft.Payload.OrderReference &&
          !draft.Payload.OrderReference.SalesOrderID
        ) {
          draft.Payload.OrderReference.SalesOrderID = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (draft.Payload.OrderReference?.SalesOrderID) {
          draft.Payload.OrderReference.SalesOrderID.Value =
            formValues.workOrderNumber
          draft.Payload.OrderReference.SalesOrderID.IsEditEnabled =
            formValues.workOrderNumberChecked
        }
      }

      // BT-15 - Goods Receipt Notification
      const goodsReceiptNotificationChanged = hasValueChanged(
        formValues.goodsReceiptNotification,
        draft.Payload.ReceiptDocumentReference?.ID?.Value?.OriginalValue,
      )
      const goodsReceiptNotificationCheckboxChanged =
        formValues.goodsReceiptNotificationChecked !==
        (eInvoice?.Payload?.ReceiptDocumentReference?.ID?.Value
          ?.IsEditEnabled || false)
      const goodsReceiptNotificationShouldEdit =
        goodsReceiptNotificationChanged ||
        goodsReceiptNotificationCheckboxChanged

      if (goodsReceiptNotificationShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.ReceiptDocumentReference) {
          draft.Payload.ReceiptDocumentReference = {} as any
        }
        if (
          draft.Payload.ReceiptDocumentReference &&
          !draft.Payload.ReceiptDocumentReference.ID
        ) {
          draft.Payload.ReceiptDocumentReference.ID = {} as any
        }
        if (
          draft.Payload.ReceiptDocumentReference?.ID &&
          !draft.Payload.ReceiptDocumentReference.ID.Value
        ) {
          draft.Payload.ReceiptDocumentReference.ID.Value = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (draft.Payload.ReceiptDocumentReference?.ID?.Value) {
          draft.Payload.ReceiptDocumentReference.ID.Value.Value =
            formValues.goodsReceiptNotification
          draft.Payload.ReceiptDocumentReference.ID.Value.IsEditEnabled =
            formValues.goodsReceiptNotificationChecked
        }
      }

      // BT-16 - Shipping Notification
      const shippingNotificationChanged = hasValueChanged(
        formValues.shippingNotification,
        draft.Payload.DespatchDocumentReference?.ID?.Value?.OriginalValue,
      )
      const shippingNotificationCheckboxChanged =
        formValues.shippingNotificationChecked !==
        (eInvoice?.Payload?.DespatchDocumentReference?.ID?.Value
          ?.IsEditEnabled || false)
      const shippingNotificationShouldEdit =
        shippingNotificationChanged || shippingNotificationCheckboxChanged

      if (shippingNotificationShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.DespatchDocumentReference) {
          draft.Payload.DespatchDocumentReference = {} as any
        }
        if (
          draft.Payload.DespatchDocumentReference &&
          !draft.Payload.DespatchDocumentReference.ID
        ) {
          draft.Payload.DespatchDocumentReference.ID = {} as any
        }
        if (
          draft.Payload.DespatchDocumentReference?.ID &&
          !draft.Payload.DespatchDocumentReference.ID.Value
        ) {
          draft.Payload.DespatchDocumentReference.ID.Value = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (draft.Payload.DespatchDocumentReference?.ID?.Value) {
          draft.Payload.DespatchDocumentReference.ID.Value.Value =
            formValues.shippingNotification
          draft.Payload.DespatchDocumentReference.ID.Value.IsEditEnabled =
            formValues.shippingNotificationChecked
        }
      }

      // BT-17 - Tender
      const tenderChanged = hasValueChanged(
        formValues.tender,
        draft.Payload.OriginatorDocumentReference?.ID?.Value?.OriginalValue,
      )
      const tenderCheckboxChanged =
        formValues.tenderChecked !==
        (eInvoice?.Payload?.OriginatorDocumentReference?.ID?.Value
          ?.IsEditEnabled || false)
      const tenderShouldEdit = tenderChanged || tenderCheckboxChanged

      if (tenderShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.OriginatorDocumentReference) {
          draft.Payload.OriginatorDocumentReference = {} as any
        }
        if (
          draft.Payload.OriginatorDocumentReference &&
          !draft.Payload.OriginatorDocumentReference.ID
        ) {
          draft.Payload.OriginatorDocumentReference.ID = {} as any
        }
        if (
          draft.Payload.OriginatorDocumentReference?.ID &&
          !draft.Payload.OriginatorDocumentReference.ID.Value
        ) {
          draft.Payload.OriginatorDocumentReference.ID.Value = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (draft.Payload.OriginatorDocumentReference?.ID?.Value) {
          draft.Payload.OriginatorDocumentReference.ID.Value.Value =
            formValues.tender
          draft.Payload.OriginatorDocumentReference.ID.Value.IsEditEnabled =
            formValues.tenderChecked
        }
      }

      // BT-18 - Object Reference
      const objectReferenceEntry =
        draft.Payload.AdditionalDocumentReference?.find(
          entry => entry?.DocumentTypeCode?.Value === '130',
        )
      const objectReferenceChanged = hasValueChanged(
        formValues.objectReference,
        objectReferenceEntry?.ID?.Value?.OriginalValue,
      )
      const objectReferenceCheckboxChanged =
        formValues.objectReferenceChecked !==
        (objectReferenceEntry?.ID?.Value?.IsEditEnabled || false)
      const objectReferenceShouldEdit =
        objectReferenceChanged || objectReferenceCheckboxChanged

      if (objectReferenceShouldEdit) {
        hasAnyChanges = true

        // Find the index of the entry with DocumentTypeCode === '130'
        const existingEntryIndex =
          draft.Payload.AdditionalDocumentReference?.findIndex(
            entry => entry?.DocumentTypeCode?.Value === '130',
          )
        const shouldUpdateExisting =
          existingEntryIndex !== undefined && existingEntryIndex !== -1

        if (shouldUpdateExisting) {
          // Update existing entry with DocumentTypeCode '130'
          if (
            draft.Payload.AdditionalDocumentReference?.[existingEntryIndex]?.ID
              ?.Value
          ) {
            draft.Payload.AdditionalDocumentReference![
              existingEntryIndex
            ].ID!.Value!.Value = formValues.objectReference
            draft.Payload.AdditionalDocumentReference![
              existingEntryIndex
            ].ID!.Value!.IsEditEnabled = formValues.objectReferenceChecked
          }

          // Handle DocumentTypeCode based on objectReferenceChecked status
          if (formValues.objectReferenceChecked && formValues.objectReference) {
            // Keep DocumentTypeCode as '130'
            if (
              draft.Payload.AdditionalDocumentReference?.[existingEntryIndex]
                ?.DocumentTypeCode
            ) {
              draft.Payload.AdditionalDocumentReference![
                existingEntryIndex
              ].DocumentTypeCode!.Value = '130'
            }
          } else if (!formValues.objectReferenceChecked) {
            // Reset DocumentTypeCode to original value when field is unchecked
            if (
              draft.Payload.AdditionalDocumentReference?.[existingEntryIndex]
                ?.DocumentTypeCode
            ) {
              const originalDocTypeCode =
                eInvoice?.Payload?.AdditionalDocumentReference?.find(
                  entry => entry?.DocumentTypeCode?.Value === '130',
                )?.DocumentTypeCode?.OriginalValue
              draft.Payload.AdditionalDocumentReference![
                existingEntryIndex
              ].DocumentTypeCode!.Value = originalDocTypeCode || ''
            }
          }
        } else {
          // Create a new entry for DocumentTypeCode '130'
          if (!draft.Payload.AdditionalDocumentReference) {
            draft.Payload.AdditionalDocumentReference = [] as any
          }

          // Create new entry
          const newEntry = {
            ID: {
              Value: {
                Value: formValues.objectReference,
                OriginalValue: '',
                IsEditEnabled: formValues.objectReferenceChecked,
              },
            },
            DocumentTypeCode: {
              Value: '130',
              OriginalValue: '',
              IsEditEnabled: false,
            },
          } as any

          // Add the new entry to the array
          if (draft.Payload.AdditionalDocumentReference) {
            draft.Payload.AdditionalDocumentReference.push(newEntry)
          }
        }
      }

      // BT-19 - Booking Account
      const bookingAccountChanged = hasValueChanged(
        formValues.bookingAccount,
        draft.Payload.AccountingCost?.OriginalValue,
      )
      const bookingAccountCheckboxChanged =
        formValues.bookingAccountChecked !==
        (eInvoice?.Payload.AccountingCost?.IsEditEnabled || false)
      const bookingAccountShouldEdit =
        bookingAccountChanged || bookingAccountCheckboxChanged

      if (bookingAccountShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.AccountingCost) {
          draft.Payload.AccountingCost = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (draft.Payload.AccountingCost) {
          draft.Payload.AccountingCost.Value = formValues.bookingAccount
          draft.Payload.AccountingCost.IsEditEnabled =
            formValues.bookingAccountChecked
        }
      }

      // BT-22 - Additional Remarks
      const additionalRemarksChanged = hasValueChanged(
        formValues.additionalRemarks,
        draft.Payload.Note?.OriginalValue,
      )
      const additionalRemarksCheckboxChanged =
        formValues.additionalRemarksChecked !==
        (eInvoice?.Payload?.Note?.IsEditEnabled || false)
      const additionalRemarksShouldEdit =
        additionalRemarksChanged || additionalRemarksCheckboxChanged

      if (additionalRemarksShouldEdit) {
        // Ensure all parent objects exist
        if (!draft.Payload.Note) {
          draft.Payload.Note = {
            Value: '',
            OriginalValue: '',
            IsEditEnabled: false,
          } as any
        }

        hasAnyChanges = true

        if (draft.Payload.Note) {
          draft.Payload.Note.Value = formValues.additionalRemarks
          draft.Payload.Note.IsEditEnabled = formValues.additionalRemarksChecked
        }
      }
    })

    if (hasAnyChanges && eInvoice?.id) {
      await requestUpdateEInvoice(eInvoice.id, eInvoiceDto)
    }
  }

  // Formik setup
  const formik = useFormik<AdditionalDataFormValues>({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialTouched: {
      // Set all fields to touched to show validation errors on load
      buyerEmail: true,
      buyerEmailChecked: true,
      buyerName: true,
      buyerNameChecked: true,
      buyerRoutingId: true,
      buyerRoutingIdChecked: true,
      buyerPhone: true,
      buyerPhoneChecked: true,
      projectNumber: true,
      projectNumberChecked: true,
      contractNumber: true,
      contractNumberChecked: true,
      orderNumber: true,
      orderNumberChecked: true,
      workOrderNumber: true,
      workOrderNumberChecked: true,
      goodsReceiptNotification: true,
      goodsReceiptNotificationChecked: true,
      shippingNotification: true,
      shippingNotificationChecked: true,
      tender: true,
      tenderChecked: true,
      objectReference: true,
      objectReferenceChecked: true,
      bookingAccount: true,
      bookingAccountChecked: true,
      additionalRemarks: true,
      additionalRemarksChecked: true,
    },
    initialValues: {
      // BT-58
      buyerEmail:
        eInvoice?.Payload?.AccountingCustomerParty.Party?.Contact
          ?.ElectronicMail?.Value || '',
      buyerEmailChecked:
        eInvoice?.Payload?.AccountingCustomerParty.Party?.Contact
          ?.ElectronicMail?.IsEditEnabled || false,
      // BT-56
      buyerName:
        eInvoice?.Payload.AccountingCustomerParty.Party?.Contact?.Name?.Value ||
        '',
      buyerNameChecked:
        eInvoice?.Payload.AccountingCustomerParty.Party?.Contact?.Name
          ?.IsEditEnabled || false,

      // BT-10
      buyerRoutingId: eInvoice?.Payload.BuyerReference?.Value || '',
      buyerRoutingIdChecked:
        eInvoice?.Payload.BuyerReference?.IsEditEnabled || false,
      // BT-57
      buyerPhone:
        eInvoice?.Payload?.AccountingCustomerParty.Party?.Contact?.Telephone
          ?.Value || '',
      buyerPhoneChecked:
        eInvoice?.Payload?.AccountingCustomerParty.Party?.Contact?.Telephone
          ?.IsEditEnabled || false,
      // BT-11
      projectNumber:
        eInvoice?.Payload?.ProjectReference?.ID?.Value?.Value || '',
      projectNumberChecked:
        eInvoice?.Payload?.ProjectReference?.ID?.Value?.IsEditEnabled || false,
      // BT-12
      contractNumber:
        eInvoice?.Payload?.ContractDocumentReference?.ID?.Value?.Value || '',
      contractNumberChecked:
        eInvoice?.Payload?.ContractDocumentReference?.ID?.Value
          ?.IsEditEnabled || false,
      // BT-13
      orderNumber: eInvoice?.Payload?.OrderReference?.ID?.Value?.Value || '',
      orderNumberChecked:
        eInvoice?.Payload?.OrderReference?.ID?.Value?.IsEditEnabled || false,
      // BT-14
      workOrderNumber:
        eInvoice?.Payload?.OrderReference?.SalesOrderID?.Value || '',
      workOrderNumberChecked:
        eInvoice?.Payload?.OrderReference?.SalesOrderID?.IsEditEnabled || false,
      // BT-15
      goodsReceiptNotification:
        eInvoice?.Payload?.ReceiptDocumentReference?.ID?.Value?.Value || '',
      goodsReceiptNotificationChecked:
        eInvoice?.Payload?.ReceiptDocumentReference?.ID?.Value?.IsEditEnabled ||
        false,
      // BT-16
      shippingNotification:
        eInvoice?.Payload?.DespatchDocumentReference?.ID?.Value?.Value || '',
      shippingNotificationChecked:
        eInvoice?.Payload?.DespatchDocumentReference?.ID?.Value
          ?.IsEditEnabled || false,
      // BT-17
      tender:
        eInvoice?.Payload?.OriginatorDocumentReference?.ID?.Value?.Value || '',
      tenderChecked:
        eInvoice?.Payload?.OriginatorDocumentReference?.ID?.Value
          ?.IsEditEnabled || false,
      // BT-18
      objectReference:
        eInvoice?.Payload?.AdditionalDocumentReference?.find(
          entry => entry?.DocumentTypeCode?.Value === '130',
        )?.ID?.Value?.Value || '',
      objectReferenceChecked:
        eInvoice?.Payload?.AdditionalDocumentReference?.find(
          entry => entry?.DocumentTypeCode?.Value === '130',
        )?.ID?.Value?.IsEditEnabled || false,
      // BT-19
      bookingAccount: eInvoice?.Payload.AccountingCost?.Value || '',
      bookingAccountChecked:
        eInvoice?.Payload.AccountingCost?.IsEditEnabled || false,
      // BT-22
      additionalRemarks: eInvoice?.Payload?.Note?.Value || '',
      additionalRemarksChecked: eInvoice?.Payload?.Note?.IsEditEnabled || false,
    },
    validate: formData => {
      let errors: FormikErrors<AdditionalDataFormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        // BT-58
        {
          fieldName: 'buyerEmail',
          config: config.eInvoice.additionalData.client.buyerEmail,
        },
        // BT-56
        {
          fieldName: 'buyerName',
          config: config.eInvoice.additionalData.client.buyerName,
        },
        // BT-10
        {
          fieldName: 'buyerRoutingId',
          config: config.eInvoice.additionalData.client.buyerRoutingId,
        },
        // BT-57
        {
          fieldName: 'buyerPhone',
          config: config.eInvoice.additionalData.client.buyerPhone,
        },
        // BT-11
        {
          fieldName: 'projectNumber',
          config:
            config.eInvoice.additionalData.invoiceReferences.projectNumber,
        },
        // BT-12
        {
          fieldName: 'contractNumber',
          config:
            config.eInvoice.additionalData.invoiceReferences.contractNumber,
        },
        // BT-13
        {
          fieldName: 'orderNumber',
          config: config.eInvoice.additionalData.invoiceReferences.orderNumber,
        },
        // BT-14
        {
          fieldName: 'workOrderNumber',
          config:
            config.eInvoice.additionalData.invoiceReferences.workOrderNumber,
        },
        // BT-15
        {
          fieldName: 'goodsReceiptNotification',
          config:
            config.eInvoice.additionalData.invoiceReferences
              .goodsReceiptNotification,
        },
        // BT-16
        {
          fieldName: 'shippingNotification',
          config:
            config.eInvoice.additionalData.invoiceReferences
              .shippingNotification,
        },
        // BT-17
        {
          fieldName: 'tender',
          config: config.eInvoice.additionalData.invoiceReferences.tender,
        },
        // BT-18
        {
          fieldName: 'objectReference',
          config:
            config.eInvoice.additionalData.invoiceReferences.objectReference,
        },
        // BT-19
        {
          fieldName: 'bookingAccount',
          config:
            config.eInvoice.additionalData.invoiceReferences.bookingAccount,
        },
        // BT-22
        {
          fieldName: 'additionalRemarks',
          config: config.eInvoice.additionalData.remarks.additionalRemarks,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      // BT-58 - Email validation
      if (formData.buyerEmailChecked) {
        if (!emailPattern.test(formData.buyerEmail)) {
          errors.buyerEmail = 'Ungültige E-Mail-Adresse'
          // t(
          //   'stepper.additionalDocuments.table.upload.dialog.form.attachment.validation.fileHasSameName',
          // )
        }
      }

      // BT-10
      if (formData.buyerRoutingIdChecked) {
        // Check email
        if (!emailPattern.test(formData.buyerRoutingId)) {
          errors.buyerRoutingId = 'Ungültige E-Mail-Adresse'
          // t(
          //   'stepper.additionalDocuments.table.upload.dialog.form.attachment.validation.fileHasSameName',
          // )
        }
      }

      return errors
    },
    onSubmit: async data => {
      if (!formik.dirty) return

      try {
        setIsRequestRunning(true)

        await handleSubmit(data)

        showToast({
          severity: 'success',
          summary: t(
            'eInvoiceDetails.stepper.additionalData.save.messages.success.summary',
          ),
          detail: t(
            'eInvoiceDetails.stepper.additionalData.save.messages.success.detail',
          ),
        })
      } catch (error) {
        const handleableError = getHandleableError(error)
        const errorMessage = handleableError.message

        showToast({
          severity: 'error',
          summary: t(
            'eInvoiceDetails.stepper.additionalData.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
      }
    },
  })

  // Block navigation when the form is dirty
  useEffect(() => {
    if (lastBlockerState.current !== formik.dirty) {
      lastBlockerState.current = formik.dirty
      setBlocker(formik.dirty)
    }
  }, [formik.dirty, setBlocker])

  const showError = (name: FormFieldName) => {
    return !!(formik.touched[name] && formik.errors[name])
  }

  const isFormFieldValid = (name: FormFieldName) => showError(name)

  const getFormErrorMessage = (name: FormFieldName) => {
    return isFormFieldValid(name) ? (
      <small className="p-error">{formik.errors[name]}</small>
    ) : undefined
  }

  const helperBadge = (tooltip: string) => (
    <span
      className="custom-target-icon pi pi-question-circle ml-2"
      data-pr-tooltip={tooltip}
      data-pr-position="top"
      data-pr-at="center top-10"
    ></span>
  )

  // Buyer email
  const buyerEmailLabel = (
    <label
      htmlFor="buyerEmail"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('buyerEmail'),
      })}
    >
      {t('eInvoiceDetails.stepper.additionalData.client.form.buyerEmail.label')}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.client.form.buyerEmail.tooltip',
        ),
      )}
    </label>
  )

  const valueTemplate = (option: FieldOption, props: any) => {
    if (option) {
      return <>{itemTemplate(option)}</>
    }

    return <>{props.placeholder}</>
  }

  const itemTemplate = (option: FieldOption) => <>{option.name.de}</>

  const buyerEmailComponent = (
    <DropdownToggleable
      id="buyerEmail"
      editable
      checked={formik.values.buyerEmailChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('buyerEmailChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'buyerEmail',
            eInvoice?.Payload?.AccountingCustomerParty.Party?.Contact
              ?.ElectronicMail?.OriginalValue || '',
          )
          formik.setFieldTouched('buyerEmail', false)
        }
      }}
      options={config.eInvoice.additionalData.client.buyerEmail.options || []}
      optionLabel="code"
      optionValue="code"
      valueTemplate={valueTemplate}
      itemTemplate={itemTemplate}
      value={formik.values.buyerEmail}
      onChange={e => formik.setFieldValue('buyerEmail', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('buyerEmail'),
      })}
    />
  )

  const buyerEmailErrorMsg = getFormErrorMessage('buyerEmail')

  const buyerEmailWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.client.buyerEmail}
      label={buyerEmailLabel}
      component={buyerEmailComponent}
      errorMsg={buyerEmailErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Buyer name
  const buyerNameLabel = (
    <label
      htmlFor="buyerName"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('buyerName'),
      })}
    >
      {t('eInvoiceDetails.stepper.additionalData.client.form.buyerName.label')}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.client.form.buyerName.tooltip',
        ),
      )}
    </label>
  )

  const buyerNameComponent = (
    <InputTextToggleable
      id="buyerName"
      checked={formik.values.buyerNameChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('buyerNameChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'buyerName',
            eInvoice?.Payload.AccountingCustomerParty.Party?.Contact?.Name
              ?.OriginalValue || '',
          )
          formik.setFieldTouched('buyerName', false)
        }
      }}
      value={formik.values.buyerName}
      onChange={e => formik.setFieldValue('buyerName', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('buyerName'),
      })}
    />
  )

  const buyerNameErrorMsg = getFormErrorMessage('buyerName')

  const buyerNameWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.client.buyerName}
      label={buyerNameLabel}
      component={buyerNameComponent}
      errorMsg={buyerNameErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Buyer routing id
  const buyerRoutingIdLabel = (
    <label
      htmlFor="buyerRoutingId"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('buyerRoutingId'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.client.form.buyerRoutingId.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.client.form.buyerRoutingId.tooltip',
        ),
      )}
    </label>
  )

  const buyerRoutingIdComponent = (
    <InputTextToggleable
      id="buyerRoutingId"
      checked={formik.values.buyerRoutingIdChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('buyerRoutingIdChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'buyerRoutingId',
            eInvoice?.Payload.BuyerReference?.OriginalValue || '',
          )
          formik.setFieldTouched('buyerRoutingId', false)
        }
      }}
      value={formik.values.buyerRoutingId}
      onChange={e => formik.setFieldValue('buyerRoutingId', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('buyerRoutingId'),
      })}
    />
  )

  const buyerRoutingIdErrorMsg = getFormErrorMessage('buyerRoutingId')

  const buyerRoutingIdWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.client.buyerRoutingId}
      label={buyerRoutingIdLabel}
      component={buyerRoutingIdComponent}
      errorMsg={buyerRoutingIdErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Buyer phone
  const buyerPhoneLabel = (
    <label
      htmlFor="buyerPhone"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('buyerPhone'),
      })}
    >
      {t('eInvoiceDetails.stepper.additionalData.client.form.buyerPhone.label')}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.client.form.buyerPhone.tooltip',
        ),
      )}
    </label>
  )

  const buyerPhoneComponent = (
    <InputTextToggleable
      id="buyerPhone"
      checked={formik.values.buyerPhoneChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('buyerPhoneChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'buyerPhone',
            eInvoice?.Payload?.AccountingCustomerParty.Party?.Contact?.Telephone
              ?.OriginalValue || '',
          )
          formik.setFieldTouched('buyerPhone', false)
        }
      }}
      value={formik.values.buyerPhone}
      onChange={e => formik.setFieldValue('buyerPhone', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('buyerPhone'),
      })}
    />
  )

  const buyerPhoneErrorMsg = getFormErrorMessage('buyerPhone')

  const buyerPhoneWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.client.buyerPhone}
      label={buyerPhoneLabel}
      component={buyerPhoneComponent}
      errorMsg={buyerPhoneErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Project number
  const projectNumberLabel = (
    <label
      htmlFor="projectNumber"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('projectNumber'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.projectNumber.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.projectNumber.tooltip',
        ),
      )}
    </label>
  )

  const projectNumberComponent = (
    <InputTextToggleable
      id="projectNumber"
      checked={formik.values.projectNumberChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('projectNumberChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'projectNumber',
            eInvoice?.Payload?.ProjectReference?.ID?.Value?.OriginalValue || '',
          )
          formik.setFieldTouched('projectNumber', false)
        }
      }}
      value={formik.values.projectNumber}
      onChange={e => formik.setFieldValue('projectNumber', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('projectNumber'),
      })}
    />
  )

  const projectNumberErrorMsg = getFormErrorMessage('projectNumber')

  const projectNumberWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.invoiceReferences.projectNumber}
      label={projectNumberLabel}
      component={projectNumberComponent}
      errorMsg={projectNumberErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Contract number
  const contractNumberLabel = (
    <label
      htmlFor="contractNumber"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('contractNumber'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.contractNumber.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.contractNumber.tooltip',
        ),
      )}
    </label>
  )

  const contractNumberComponent = (
    <InputTextToggleable
      id="contractNumber"
      checked={formik.values.contractNumberChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('contractNumberChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'contractNumber',
            eInvoice?.Payload?.ContractDocumentReference?.ID?.Value
              ?.OriginalValue || '',
          )
          formik.setFieldTouched('contractNumber', false)
        }
      }}
      value={formik.values.contractNumber}
      onChange={e => formik.setFieldValue('contractNumber', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('contractNumber'),
      })}
    />
  )

  const contractNumberErrorMsg = getFormErrorMessage('contractNumber')

  const contractNumberWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.invoiceReferences.contractNumber}
      label={contractNumberLabel}
      component={contractNumberComponent}
      errorMsg={contractNumberErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Order number
  const orderNumberLabel = (
    <label
      htmlFor="orderNumber"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('orderNumber'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.orderNumber.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.orderNumber.tooltip',
        ),
      )}
    </label>
  )

  const orderNumberComponent = (
    <InputTextToggleable
      id="orderNumber"
      checked={formik.values.orderNumberChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('orderNumberChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'orderNumber',
            eInvoice?.Payload?.OrderReference?.ID?.Value?.OriginalValue || '',
          )
          formik.setFieldTouched('orderNumber', false)
        }
      }}
      value={formik.values.orderNumber}
      onChange={e => formik.setFieldValue('orderNumber', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('orderNumber'),
      })}
    />
  )

  const orderNumberErrorMsg = getFormErrorMessage('orderNumber')

  const orderNumberWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.invoiceReferences.orderNumber}
      label={orderNumberLabel}
      component={orderNumberComponent}
      errorMsg={orderNumberErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Work order number
  const workOrderNumberLabel = (
    <label
      htmlFor="workOrderNumber"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('workOrderNumber'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.workOrderNumber.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.workOrderNumber.tooltip',
        ),
      )}
    </label>
  )

  const workOrderNumberComponent = (
    <InputTextToggleable
      id="workOrderNumber"
      checked={formik.values.workOrderNumberChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('workOrderNumberChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'workOrderNumber',
            eInvoice?.Payload?.OrderReference?.SalesOrderID?.OriginalValue ||
              '',
          )
          formik.setFieldTouched('workOrderNumber', false)
        }
      }}
      value={formik.values.workOrderNumber}
      onChange={e => formik.setFieldValue('workOrderNumber', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('workOrderNumber'),
      })}
    />
  )

  const workOrderNumberErrorMsg = getFormErrorMessage('workOrderNumber')

  const workOrderNumberWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.invoiceReferences.workOrderNumber}
      label={workOrderNumberLabel}
      component={workOrderNumberComponent}
      errorMsg={workOrderNumberErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Goods receipt notification
  const goodsReceiptNotificationLabel = (
    <label
      htmlFor="goodsReceiptNotification"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('goodsReceiptNotification'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.goodsReceiptNotification.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.goodsReceiptNotification.tooltip',
        ),
      )}
    </label>
  )

  const goodsReceiptNotificationComponent = (
    <InputTextToggleable
      id="goodsReceiptNotification"
      checked={formik.values.goodsReceiptNotificationChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('goodsReceiptNotificationChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'goodsReceiptNotification',
            eInvoice?.Payload?.ReceiptDocumentReference?.ID?.Value
              ?.OriginalValue || '',
          )
          formik.setFieldTouched('goodsReceiptNotification', false)
        }
      }}
      value={formik.values.goodsReceiptNotification}
      onChange={e =>
        formik.setFieldValue('goodsReceiptNotification', e.target.value)
      }
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('goodsReceiptNotification'),
      })}
    />
  )

  const goodsReceiptNotificationErrorMsg = getFormErrorMessage(
    'goodsReceiptNotification',
  )

  const goodsReceiptNotificationWrapper = (
    <ConfigurableComponent
      config={
        config.eInvoice.additionalData.invoiceReferences
          .goodsReceiptNotification
      }
      label={goodsReceiptNotificationLabel}
      component={goodsReceiptNotificationComponent}
      errorMsg={goodsReceiptNotificationErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Shipping notification
  const shippingNotificationLabel = (
    <label
      htmlFor="shippingNotification"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('shippingNotification'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.shippingNotification.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.shippingNotification.tooltip',
        ),
      )}
    </label>
  )

  const shippingNotificationComponent = (
    <InputTextToggleable
      id="shippingNotification"
      checked={formik.values.shippingNotificationChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('shippingNotificationChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'shippingNotification',
            eInvoice?.Payload?.DespatchDocumentReference?.ID?.Value
              ?.OriginalValue || '',
          )
          formik.setFieldTouched('shippingNotification', false)
        }
      }}
      value={formik.values.shippingNotification}
      onChange={e =>
        formik.setFieldValue('shippingNotification', e.target.value)
      }
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('shippingNotification'),
      })}
    />
  )

  const shippingNotificationErrorMsg = getFormErrorMessage(
    'shippingNotification',
  )

  const shippingNotificationWrapper = (
    <ConfigurableComponent
      config={
        config.eInvoice.additionalData.invoiceReferences.shippingNotification
      }
      label={shippingNotificationLabel}
      component={shippingNotificationComponent}
      errorMsg={shippingNotificationErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Tender
  const tenderLabel = (
    <label
      htmlFor="tender"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('tender'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.tender.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.tender.tooltip',
        ),
      )}
    </label>
  )

  const tenderComponent = (
    <InputTextToggleable
      id="tender"
      checked={formik.values.tenderChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('tenderChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'tender',
            eInvoice?.Payload?.OriginatorDocumentReference?.ID?.Value
              ?.OriginalValue || '',
          )
          formik.setFieldTouched('tender', false)
        }
      }}
      value={formik.values.tender}
      onChange={e => formik.setFieldValue('tender', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('tender'),
      })}
    />
  )

  const tenderErrorMsg = getFormErrorMessage('tender')

  const tenderWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.invoiceReferences.tender}
      label={tenderLabel}
      component={tenderComponent}
      errorMsg={tenderErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Object reference
  const objectReferenceLabel = (
    <label
      htmlFor="objectReference"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('objectReference'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.objectReference.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.objectReference.tooltip',
        ),
      )}
    </label>
  )

  const objectReferenceComponent = (
    <InputTextToggleable
      id="objectReference"
      checked={formik.values.objectReferenceChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('objectReferenceChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'objectReference',
            eInvoice?.Payload?.AdditionalDocumentReference?.find(
              entry => entry?.DocumentTypeCode?.Value === '130',
            )?.ID?.Value?.OriginalValue || '',
          )
          formik.setFieldTouched('objectReference', false)
        }
      }}
      value={formik.values.objectReference}
      onChange={e => formik.setFieldValue('objectReference', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('objectReference'),
      })}
    />
  )

  const objectReferenceErrorMsg = getFormErrorMessage('objectReference')

  const objectReferenceWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.invoiceReferences.objectReference}
      label={objectReferenceLabel}
      component={objectReferenceComponent}
      errorMsg={objectReferenceErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Booking account
  const bookingAccountLabel = (
    <label
      htmlFor="bookingAccount"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('bookingAccount'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.bookingAccount.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.invoiceReferences.form.bookingAccount.tooltip',
        ),
      )}
    </label>
  )

  const bookingAccountComponent = (
    <InputTextToggleable
      id="bookingAccount"
      checked={formik.values.bookingAccountChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('bookingAccountChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'bookingAccount',
            eInvoice?.Payload.AccountingCost?.OriginalValue || '',
          )
          formik.setFieldTouched('bookingAccount', false)
        }
      }}
      value={formik.values.bookingAccount}
      onChange={e => formik.setFieldValue('bookingAccount', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('bookingAccount'),
      })}
    />
  )

  const bookingAccountErrorMsg = getFormErrorMessage('bookingAccount')

  const bookingAccountWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.invoiceReferences.bookingAccount}
      label={bookingAccountLabel}
      component={bookingAccountComponent}
      errorMsg={bookingAccountErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  // Additional remarks
  const additionalRemarksLabel = (
    <label
      htmlFor="additionalRemarks"
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('additionalRemarks'),
      })}
    >
      {t(
        'eInvoiceDetails.stepper.additionalData.remarks.form.additionalRemarks.label',
      )}

      {helperBadge(
        t(
          'eInvoiceDetails.stepper.additionalData.remarks.form.additionalRemarks.tooltip',
        ),
      )}
    </label>
  )

  const additionalRemarksComponent = (
    <InputTextareaToggleable
      id="additionalRemarks"
      checked={formik.values.additionalRemarksChecked}
      onCheckedChange={(e: CheckboxChangeEvent) => {
        formik.setFieldValue('additionalRemarksChecked', e.checked)
        if (!e.checked) {
          formik.setFieldValue(
            'additionalRemarks',
            eInvoice?.Payload?.Note?.OriginalValue || '',
          )
          formik.setFieldTouched('additionalRemarks', false)
        }
      }}
      value={formik.values.additionalRemarks}
      onChange={e => formik.setFieldValue('additionalRemarks', e.target.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('additionalRemarks'),
      })}
    />
  )

  const additionalRemarksErrorMsg = getFormErrorMessage('additionalRemarks')

  const additionalRemarksWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalData.remarks.additionalRemarks}
      label={additionalRemarksLabel}
      component={additionalRemarksComponent}
      errorMsg={additionalRemarksErrorMsg}
      isLoading={isLoading}
      className="mb-2"
    />
  )

  return (
    <>
      <Tooltip target=".custom-target-icon" />

      <Accordion multiple activeIndex={[0, 1, 2, 3]}>
        <AccordionTab
          header={t('eInvoiceDetails.stepper.additionalData.client.title')}
        >
          <div className="grid">
            <div className="col-12 md:col-6 xl:col-4">
              {buyerEmailWrapper}
              <ValueStatus
                trackedValue={
                  eInvoice?.Payload?.AccountingCustomerParty.Party?.Contact
                    ?.ElectronicMail
                }
                formikValue={formik.values.buyerEmail}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-12 md:col-6 xl:col-4">
              {buyerNameWrapper}
              <ValueStatus
                trackedValue={
                  eInvoice?.Payload.AccountingCustomerParty.Party?.Contact?.Name
                }
                formikValue={formik.values.buyerName}
              />
            </div>

            <div className="col-12 md:col-6 xl:col-4">
              {buyerRoutingIdWrapper}
              <ValueStatus
                trackedValue={eInvoice?.Payload.BuyerReference}
                formikValue={formik.values.buyerRoutingId}
              />
            </div>

            <div className="col-12 md:col-6 xl:col-4">
              {buyerPhoneWrapper}
              <ValueStatus
                trackedValue={
                  eInvoice?.Payload?.AccountingCustomerParty.Party?.Contact
                    ?.Telephone
                }
                formikValue={formik.values.buyerPhone}
              />
            </div>
          </div>
        </AccordionTab>

        <AccordionTab
          header={t(
            'eInvoiceDetails.stepper.additionalData.invoiceReferences.title',
          )}
        >
          <div className="grid">
            <div className="col-12 md:col-6 xl:col-4">
              {projectNumberWrapper}
              <ValueStatus
                trackedValue={eInvoice?.Payload?.ProjectReference?.ID?.Value}
                formikValue={formik.values.projectNumber}
              />
            </div>

            <div className="col-12 md:col-6 xl:col-4">
              {contractNumberWrapper}
              <ValueStatus
                trackedValue={
                  eInvoice?.Payload?.ContractDocumentReference?.ID?.Value
                }
                formikValue={formik.values.contractNumber}
              />
            </div>

            <div className="col-12 md:col-6 xl:col-4">
              {orderNumberWrapper}
              <ValueStatus
                trackedValue={eInvoice?.Payload?.OrderReference?.ID?.Value}
                formikValue={formik.values.orderNumber}
              />
            </div>

            <div className="col-12 md:col-6 xl:col-4">
              {workOrderNumberWrapper}
              <ValueStatus
                trackedValue={eInvoice?.Payload?.OrderReference?.SalesOrderID}
                formikValue={formik.values.workOrderNumber}
              />
            </div>

            <div className="col-12 md:col-6 xl:col-4">
              {goodsReceiptNotificationWrapper}
              <ValueStatus
                trackedValue={
                  eInvoice?.Payload?.ReceiptDocumentReference?.ID?.Value
                }
                formikValue={formik.values.goodsReceiptNotification}
              />
            </div>

            <div className="col-12 md:col-6 xl:col-4">
              {shippingNotificationWrapper}
              <ValueStatus
                trackedValue={
                  eInvoice?.Payload?.DespatchDocumentReference?.ID?.Value
                }
                formikValue={formik.values.shippingNotification}
              />
            </div>

            <div className="col-12 md:col-6 xl:col-4">
              {tenderWrapper}
              <ValueStatus
                trackedValue={
                  eInvoice?.Payload?.OriginatorDocumentReference?.ID?.Value
                }
                formikValue={formik.values.tender}
              />
            </div>

            <div className="col-12 md:col-6 xl:col-4">
              {objectReferenceWrapper}
              <ValueStatus
                trackedValue={
                  eInvoice?.Payload?.AdditionalDocumentReference?.find(
                    entry => entry?.DocumentTypeCode?.Value === '130',
                  )?.ID?.Value || null
                }
                formikValue={formik.values.objectReference}
              />
            </div>

            <div className="col-12 md:col-6 xl:col-4">
              {bookingAccountWrapper}
              <ValueStatus
                trackedValue={eInvoice?.Payload.AccountingCost}
                formikValue={formik.values.bookingAccount}
              />
            </div>
          </div>
        </AccordionTab>

        <AccordionTab
          header={t('eInvoiceDetails.stepper.additionalData.positions.title')}
        >
          <div className="grid">
            <div className="col-12">
              <LinesData editable={true} />
            </div>
          </div>
        </AccordionTab>

        <AccordionTab
          header={t('eInvoiceDetails.stepper.additionalData.remarks.title')}
        >
          <div className="grid">
            <div className="col-12">
              {additionalRemarksWrapper}
              <ValueStatus
                trackedValue={eInvoice?.Payload?.Note}
                formikValue={formik.values.additionalRemarks}
              />
            </div>
          </div>
        </AccordionTab>
      </Accordion>

      <Footer
        stepperRef={stepperRef}
        isRequestRunning={isRequestRunning}
        prevButtonCallback={async () => {
          setBlocker(false)
          await formik.validateForm()
          await formik.submitForm()
        }}
        nextButtonCallback={async () => {
          setBlocker(false)
          await formik.validateForm()
          await formik.submitForm()
        }}
        isPrevButtonDisabled={formik.dirty && !formik.isValid}
        isNextButtonDisabled={formik.dirty && !formik.isValid}
      />
    </>
  )
}
